import { Link } from "@/lib/nav";
import type { ReactNode } from "react";

import CustomerScreenShell from "@/components/customer-screen-shell";
import { sectionCardClass } from "@buildpy/ui/section-card";
import { cn } from "@/lib/utils";
import { intakeFromPublicPath, withVia } from "@buildpy/core/modules/customer/intake-page";
import { publicPortfolioDetailPath } from "@buildpy/core/modules/portfolio/public-view";
import PublicCaseGrid, { publicCaseTraits } from "@/modules/portfolio/ui/public-case-grid";
import type { PublicPageView } from "@buildpy/core/modules/public-page/link-service";
import {
  HeroPhotos,
  PublicPageCta,
  PublicPageDesigners,
  PublicPageEmpty,
  PublicPageFooter,
  PublicPageHeader,
  PublicPageReviews,
  PublicPageTrustCard,
} from "@/modules/public-page/ui/public-page-sections";

// 공개 링크 A(사진 그리드) — 대표 사례 → 그리드 → 신뢰 지표(자동 집계만) → 담당 디자이너 → 고객 후기 → 상담 문의.
const PublicPageGrid = ({ view, footer }: { view: PublicPageView; footer?: ReactNode }) => {
  const { org, code, hero, cards, trust, reviews, designers, show_inquiry } = view;
  const inquiry = show_inquiry ? intakeFromPublicPath(org.slug, code, null) : null;
  const detailOf = (id: string) => withVia(publicPortfolioDetailPath(org.slug, id), code);
  const heroTraits = hero ? publicCaseTraits(hero) : "";

  return (
    <CustomerScreenShell className="p-0 pb-10">
      <PublicPageHeader inquiry={inquiry} org={org} />

      <div className="mx-auto flex w-full max-w-5xl flex-col gap-y-8 px-4 pt-4">
        {hero && (
          <Link
            className={cn(sectionCardClass({ padding: "none" }), "group block overflow-hidden")}
            data-testid="public-page-hero"
            href={detailOf(hero.id)}>
            <HeroPhotos hero={hero} />
            <div className="flex flex-col gap-y-1.5 p-4">
              <h2 className="text-lg font-semibold group-hover:underline">{hero.title}</h2>
              {heroTraits && <p className="text-sm text-muted-foreground">{heroTraits}</p>}
              {hero.summary && <p className="line-clamp-3 text-sm text-muted-foreground">{hero.summary}</p>}
            </div>
          </Link>
        )}

        {cards.length > 0 && <PublicCaseGrid cards={cards} slug={org.slug} via={code} />}
        {!hero && cards.length === 0 && <PublicPageEmpty />}

        <PublicPageTrustCard trust={trust} />
        <PublicPageDesigners designers={designers} />
        <PublicPageReviews reviews={reviews} />
        {inquiry && <PublicPageCta inquiry={inquiry} />}
        <PublicPageFooter org={org} />
      </div>
      {footer}
    </CustomerScreenShell>
  );
};

export default PublicPageGrid;
